import { useEffect, useState } from 'react';
import { CreditCard, Check, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

/**
 * Direct Deposit — how a practitioner or vendor actually gets paid.
 *
 * This lived only in the Contributor dashboard under Earnings, which is not where a
 * practitioner or a storefront owner looks for it: "I cannot find it anywhere!" (owner,
 * Jun 2026). It now sits on the Hub Overview, the first screen they see.
 *
 * It reads the contributor status endpoint on purpose — that one resolves the connected
 * account across users, vendors AND practitioners, so a person with several roles sees one
 * bank setup instead of being sent round Stripe's identity checks twice.
 */
export default function DirectDepositCard({ prelaunch }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/contributor/stripe/status`, { headers });
      setStatus(res.data);
    } catch {
      setStatus({ has_account: false, unavailable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'onboarded' || params.get('stripe') === 'refresh') {
      setTimeout(load, 1500);
    }
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/contributor/stripe/onboard`, {}, { headers });
      window.location.href = res.data.onboarding_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't open Stripe just now");
      setStarting(false);
    }
  };

  const wrap = (children) => (
    <div
      data-testid="direct-deposit-card"
      style={{
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14, padding: 18, marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <CreditCard size={16} style={{ color: '#6ee7c7' }} />
        <strong style={{ fontSize: 15, color: '#f5f0e8' }}>Direct deposit</strong>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return wrap(
      <Loader2 size={16} className="animate-spin" style={{ color: '#6ee7c7', marginTop: 8 }} />
    );
  }

  if (status?.onboarded) {
    return wrap(
      <div data-testid="direct-deposit-connected">
        <p style={{ fontSize: 13, color: '#6ee7c7', display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0 10px' }}>
          <Check size={14} /> Connected — your money lands in your bank 1–2 business days after each release.
        </p>
        <button
          onClick={start}
          disabled={starting}
          data-testid="direct-deposit-update-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'none',
            fontSize: 12, cursor: 'pointer', color: 'rgba(245,240,232,0.75)',
          }}
        >
          {starting ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
          Update bank details
        </button>
      </div>
    );
  }

  return wrap(
    <div data-testid="direct-deposit-setup">
      <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.7)', margin: '6px 0 10px', lineHeight: 1.55 }}>
        {status?.has_account
          ? 'Stripe still needs a few details before it can pay you.'
          : 'Connect your bank so your earnings come straight to you. Stripe collects your address and tax details as part of this, and your tax documents come from there too.'}
        {status?.requirements_due?.length > 0 && (
          <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,240,232,0.45)', marginTop: 4 }}>
            Still needed: {status.requirements_due.join(', ')}
          </span>
        )}
      </p>

      {prelaunch && !status?.live_mode && (
        <p style={{
          fontSize: 12, color: '#f0c05a', background: 'rgba(240,192,90,0.08)',
          border: '1px solid rgba(240,192,90,0.2)', borderRadius: 8,
          padding: '9px 11px', margin: '0 0 10px', lineHeight: 1.5,
        }} data-testid="direct-deposit-prelaunch-note">
          <AlertCircle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
          We're still in pre-launch, so hold off on this one — bank details entered now are in
          Stripe's test mode and won't carry over. We'll email you the day it counts.
        </p>
      )}

      {/* Live keys are in, so bank details DO carry over. The old notice was gated on the
          pre-launch SUBSCRIPTION flag, which says nothing about Stripe's mode, so it survived
          the live cutover and told practitioners to skip the one step that matters. */}
      {prelaunch && status?.live_mode && (
        <p style={{
          fontSize: 12, color: 'rgba(245,240,232,0.62)', background: 'rgba(255,255,255,0.03)',
          borderRadius: 8, padding: '9px 11px', margin: '0 0 10px', lineHeight: 1.55,
        }} data-testid="direct-deposit-live-note">
          Setting this up is free, and it counts — what you enter now is the account you will be
          paid into. Taking bookings in the app becomes a subscription after launch day. Your
          external booking link and your storefront stay free.
        </p>
      )}

      <button
        onClick={start}
        disabled={starting}
        data-testid="direct-deposit-start-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px',
          borderRadius: 8, border: 'none', background: '#1f7a5c', color: '#fff',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {starting ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
        {status?.has_account ? 'Finish Stripe setup' : 'Set up direct deposit'}
      </button>
    </div>
  );
}
